/**
 * The theme the Cloud designer is given: the workspace's, with Cloud's fonts
 * declared in it.
 *
 * Cloud loads its four families with `@font-face` rules of its own (`main.tsx`) and
 * the render service embeds them from its own configuration, so the themes the
 * service hands out declare no `fonts`. That was invisible until the designer began
 * reading `theme.fonts` for everything font-shaped: its pickers now offer exactly
 * what a theme declares, so a Cloud designer offered no family at all, and marked an
 * imported document's Inter "not in this theme" although Cloud embeds Inter.
 *
 * Declaring the faces here, in the tab and nowhere else, is what fixes the pickers
 * and lets the engine that measures Preview read the same files without being
 * handed them separately. The service is untouched: it never sees this theme, and
 * would refuse a request's relative font paths if it did.
 */
import { CLOUD_FONT_FACES } from "@broadpaper/cloud-fonts";
import type { Theme } from "@broadpaper/core";

export function designerTheme(theme: Theme): Theme {
  return {
    ...theme,
    typography: {
      ...theme.typography,
      // Deliberately present and undefined. The designer merges a theme over the
      // default one, whose mono font is JetBrains Mono — which Cloud does not embed
      // and no block Cloud offers uses — so an absent key would bring it back as a
      // choice in every font picker. An own property set to undefined wins the merge.
      monoFont: undefined
    },
    fonts: [...(theme.fonts ?? []), ...CLOUD_FONT_FACES.map((face) => ({ family: face.family, src: `/fonts/${face.file}`, weight: face.weight, style: face.style }))]
  };
}
